import type { HairlineFigure } from "./Hairline";
import {
  Cam,
  clamp,
  facing,
  fit,
  poly,
  prism,
  proj,
  rings,
  rrect,
  seg,
} from "./iso";
import { tdone, tset, tval, tween } from "./motion";
import {
  disposer,
  mk,
  pointer,
  put,
  register,
  solid,
  type FigureEls,
  type FigureHandle,
} from "./stage";

/**
 * Shelf: six ledgers standing spine out on a plank between two bookends.
 * The book under the pointer slides out toward you, and its neighbours come
 * out after it, less the further away, staggered outwards on the 700ms lift
 * curve. Each spine carries its bands and a blank label.
 * At rest a few books stand proud by different amounts, one of them bright.
 * The slider is how far the book comes out.
 *
 * The pattern: one of many, as Riffle. Tweens, a stagger and a falloff by
 * distance, and a hit test on the books' rest centres along the row.
 */

const TH = [14, 11, 17, 13, 16, 12],
  HT = [54, 48, 62, 52, 58, 50],
  DP = [40, 36, 42, 38, 40, 36];
const N = TH.length,
  GAP = 1.2,
  MAX = 32,
  STEP = 45;
const X0 = TH.map((_, i) => TH.slice(0, i).reduce((a, t) => a + t + GAP, 0)),
  XN = X0[N - 1] + TH[N - 1];
/** At rest: how far each book stands proud of the row, and the one that says where to look. */
const REST = [1, 4, 0, 10, 2, 0],
  MARK = 3;
/** The share of the pull a book takes at 0, 1, 2 and 3 books from the one pulled. */
const FALL = [1, 0.4, 0.15, 0];

function mount({ stage, svg, read }: FigureEls, value: number): FigureHandle {
  const bag = disposer();
  let pull = value;

  const C = Cam(45, 0.5, 2.0);
  fit(
    C,
    [
      [-12, -50, -5],
      [XN + 12, 10, -5],
      [-12, 10, -5],
      [XN + 12, -50, -5],
      [X0[2], -DP[2], HT[2]],
      [XN, MAX, 0],
      [0, MAX, 0],
    ],
    200,
    166,
  );
  const P = proj(C),
    front = facing(C);

  const g = mk("g", {}, svg);
  const [pr, pi] = rings(-12, -50, XN + 12, 10, 5, 1.6);
  put(solid(g), prism(P, front, pr, pi, -5, 0));
  /** A bookend: a thin rounded plate standing across the row, painted in its place in the order. */
  const end = (x) => {
    const [r, i] = rings(x, -30, x + 2.6, -8, 1.2, 0.5);
    put(solid(g), prism(P, front, r, i, 0, 30));
  };
  end(-4.2);

  // Left to right: a book with a larger x is always the nearer, however far out either stands.
  const books = TH.map((t, k) => {
    const el = solid(g),
      marks = mk("path", { class: "nf lo" }, el.g),
      label = mk("path", { class: "nf" }, el.g);
    return {
      k,
      t,
      h: HT[k],
      dp: DP[k],
      x: X0[k],
      el,
      marks,
      label,
      d: tween(REST[k]),
      drawn: NaN,
    };
  });
  end(XN + 1.6);

  /** A book standing d out of the row: its body, the bands across its spine, and its label. */
  function draw(b, d) {
    if (d === b.drawn) return;
    b.drawn = d;
    const { x, t, h, dp } = b,
      y = d;
    const [ring, crease] = rings(x, y - dp, x + t, y, 1.8, 0.8);
    put(b.el, prism(P, front, ring, crease, 0, h));
    const across = (z) => seg(P(x + 1, y, z), P(x + t - 1, y, z));
    b.marks.setAttribute("d", [h - 6, h - 8.5, 5, 7.5].map(across).join(""));
    b.label.setAttribute(
      "d",
      poly(
        rrect(x + 2.5, h * 0.55, x + t - 2.5, h * 0.7, 1.2, 3).map((q) =>
          P(q.u, y, q.v),
        ),
      ),
    );
  }

  const B = register(stage, (_dt, now) => {
    let moving = false;
    for (const b of books) {
      draw(b, tval(b.d, now));
      if (!tdone(b.d, now)) moving = true;
    }
    return moving;
  });
  bag.add(() => B.unregister());

  // The hit test: the book whose rest centre is nearest the pointer across the screen, inside the row's rest box.
  const cx = books.map((b) => P(b.x + b.t / 2, -b.dp / 2, b.h / 2)[0]);
  const top = Math.min(...books.map((b) => P(b.x, -b.dp, b.h)[1])),
    bottom = Math.max(...books.map((b) => P(b.x + b.t, MAX, 0)[1]));
  function hit([sx, sy]) {
    if (
      sy < top - 8 ||
      sy > bottom + 8 ||
      sx < cx[0] - 14 ||
      sx > cx[N - 1] + 14
    )
      return -1;
    let best = 0;
    cx.forEach((c, k) => {
      if (Math.abs(c - sx) < Math.abs(cx[best] - sx)) best = k;
    });
    return best;
  }

  let act = -1;
  /** Pulls book a (-1 puts them all back): each takes its share of the pull, later the further it is from the one pulled. */
  function setActive(a) {
    if (a === act) return;
    const now = performance.now(),
      from = a >= 0 ? a : act;
    act = a;
    for (const b of books) {
      const far = Math.abs(b.k - from);
      tset(
        b.d,
        a < 0 ? REST[b.k] : pull * FALL[Math.min(far, 3)],
        now,
        far * STEP,
      );
      b.el.sil.classList.toggle("hi", a < 0 ? b.k === MARK : b.k === a);
    }
    read.textContent = a < 0 ? "rest" : `book ${a + 1}`;
    B.wake();
  }

  books[MARK].el.sil.classList.add("hi");
  bag.add(
    pointer(stage, {
      move: (p) => setActive(hit(p)),
      leave: () => setActive(-1),
    }),
  );
  bag.add(() => svg.replaceChildren());

  return {
    set: (v) => {
      pull = clamp(v, 0, MAX);
      if (act >= 0) {
        const a = act;
        act = -1;
        setActive(a);
      }
    },
    destroy: () => bag.dispose(),
  };
}

export const SHELF: HairlineFigure = {
  name: "shelf",
  means:
    "Six ledgers on a shelf: the one under the pointer slides out toward you, and its neighbours follow it, less the further away.",
  range: [12, 22, 32],
  tour: [[196, 123], [240, 146], [171, 114], null],
  mount,
};
